import React, { useContext } from 'react'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import styled from 'styled-components'
import { Flex } from '../../../Flex'
import { RecatContext } from '../../../../context/RecatContext'
import { formatISO } from '../../../../utils/date'
import { Body2, Body2Bold } from '../../../Typography'
import { spacing } from '../../../../theme'
import { getNumberFormatter } from '../../../../utils/adaptiveNumberFormat'
import { Maybe } from '../../../../graphql/generated'

const ScrollBox = styled(Flex)`
  column-gap: ${spacing.medium};
  flex-wrap: wrap;
  max-height: 200px;
  overflow: auto;
  width: 100%;
`

function SubTable({
  entries,
  keyPrefix,
  hideId,
}: {
  entries: {
    id?: Maybe<string> | number
    name?: Maybe<string>
  }[]
  keyPrefix: string
  hideId?: boolean
}): React.JSX.Element | null {
  if (entries.length > 1) {
    return (
      <ScrollBox>
        <Table size='small' style={{ borderLeft: '1px solid #d4dae0' }}>
          <colgroup>
            <col width='100px' />
            <col />
          </colgroup>
          {entries.map((e) => (
            <TableRow key={`${keyPrefix}-${e.id ?? e.name}`}>
              {!hideId && (
                <TableCell>
                  <Body2>{e.id ?? <span>okänd</span>}</Body2>
                </TableCell>
              )}
              <TableCell>
                <Body2>{e.name}</Body2>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </ScrollBox>
    )
  }
  if (entries.length === 1) {
    return (
      <Flex style={{ gap: spacing.medium }}>
        {entries.length === 1 && (
          <>
            {!hideId && <Body2>{entries[0].id ?? <span>okänd</span>}</Body2>}
            <Body2>{entries[0].name}</Body2>
          </>
        )}
      </Flex>
    )
  }

  return null
}

export function TransactionsInfoBox(): React.JSX.Element {
  const {
    affectedAccounts,
    affectedSuppliers,
    numTransactions,
    firstTransactionDate,
    lastTransactionDate,
    totalKr,
    transactionsCategories,
    totalCO2e,
  } = useContext(RecatContext)

  const formater = getNumberFormatter([totalCO2e])

  return (
    <Table size='small' style={{ borderLeft: '1px solid  #d4dae0' }}>
      <colgroup>
        <col width='200px' />
        <col />
      </colgroup>
      <TableBody>
        <TableRow>
          <TableCell>
            <Body2Bold>Antal transaktioner</Body2Bold>
          </TableCell>
          <TableCell>
            <Body2>{numTransactions}</Body2>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Body2Bold>totalt kr</Body2Bold>
          </TableCell>
          <TableCell>
            <Body2>{totalKr?.toFixed(0)}</Body2>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Body2Bold>totalt CO2e</Body2Bold>
          </TableCell>
          <TableCell>
            <Body2>{formater.format(totalCO2e)}</Body2>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Body2Bold>Första transaktionsdatum</Body2Bold>
          </TableCell>
          <TableCell>
            <Body2>
              {firstTransactionDate ? formatISO(firstTransactionDate) : ''}
            </Body2>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Body2Bold>Sista transaktionsdatum</Body2Bold>
          </TableCell>
          <TableCell>
            <Body2>
              {lastTransactionDate ? formatISO(lastTransactionDate) : ''}
            </Body2>
          </TableCell>
        </TableRow>
        {affectedSuppliers.length >= 1 && (
          <TableRow>
            <TableCell>
              <Body2Bold>Leverantörer</Body2Bold>
            </TableCell>
            <TableCell>
              <SubTable entries={affectedSuppliers} keyPrefix='supplier-info' />
            </TableCell>
          </TableRow>
        )}
        {affectedAccounts.length >= 1 && (
          <TableRow>
            <TableCell>
              <Body2Bold>Konto</Body2Bold>
            </TableCell>
            <TableCell>
              <SubTable entries={affectedAccounts} keyPrefix='account-info' />
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>
            <Body2Bold>Kategorier</Body2Bold>
          </TableCell>
          <TableCell>
            <SubTable
              entries={transactionsCategories}
              keyPrefix='category-'
              hideId
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}
